import { PrimaryButton } from '@client/new/components/button';
import { navigate, PATHS } from '@client/routes';
import { useSignUpStore } from '../../../stores/sign-up.store';
import { AuthLayout } from '../auth.layout';
import { Progress } from './progress';
import { ArrayItem, F1 } from '../../../helpers/types';
import { Typography, Checkbox, Chip } from '@mui/material';
import classNames from 'classnames';
import { track } from '@services/mixpanel';
import { useState } from 'react';
import Spinner from '@client/components/Spinner';

const options = [
  {
    value: 'subscription',
    label: 'Subscription',
    recommended: false,
    description:
      'steady and predictable income • customer loyalty • long-term relationships • content iterations • scalability  • cross-promotion',
  },
  {
    value: 'one-time-purchase',
    label: 'One Time Purchase',
    recommended: true,
    description:
      'immediate revenue • flexibility • lower barrier to entry • no retention pressure • specific content access • simplicity ',
  },
];

type Option = ArrayItem<typeof options>;
type OptionsProps = {
  values: Array<Option>;
  selected?: string;
  onSelect: F1<string>;
};
const Options = ({ values, selected, onSelect }: OptionsProps) => (
  <div className="flex flex-col gap-3">
    {values.map((option) => (
      <OptionDisplay
        option={option}
        key={option.value}
        isSelected={selected === option.value}
        onClick={() => onSelect(option.value)}
      />
    ))}
  </div>
);

function OptionDisplay({
  onClick,
  option,
  isSelected,
}: {
  onClick: () => void;
  option: Option;
  isSelected: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={classNames(
        'bg-white p-4 rounded-lg cursor-pointer',
        isSelected && 'border-primary border-2',
      )}
    >
      <div className="text-left flex items-center relative w-full">
        <Checkbox checked={isSelected} />
        <Typography variant="h4">{option.label}</Typography>
        {option.recommended ? (
          <Chip
            label="Recommended"
            className="absolute right-0 bg-primary text-white rounded-full h-7 text-xs"
          />
        ) : null}
      </div>

      <div className="text-left pl-[45px]">
        <Typography variant="body2">{option.description}</Typography>
      </div>
    </div>
  );
}

export const SalesModelStepPage = () => {
  const { setSalesModel, salesModel, email } = useSignUpStore();
  const [redirectState, setRedirectState] = useState<
    'not-started' | 'redirecting' | 'redirected'
  >('not-started');

  const complete = salesModel?.length;

  if (!email) {
    navigate(PATHS.SIGN_UP);
    return null;
  }

  const onRegister = () => {
    if (!complete) return;
    if (redirectState === 'redirecting') return;
    setRedirectState('redirecting');
    track(
      {
        type: 'Business Model Selected',
        payload: { 'Model Type': salesModel, uid: '' },
      },
      {
        cb: () => {
          if (redirectState === 'redirected') return;
          setRedirectState('redirected');
          navigate(PATHS.SIGN_UP_CONFIRMATION_CODE);
        },
      },
    );

    setTimeout(() => {
      if (redirectState === 'redirected') return;
      setRedirectState('redirected');
      navigate(PATHS.SIGN_UP_CONFIRMATION_CODE);
    }, 500);
  };

  return (
    <AuthLayout showGoBack title={'Which business model do you prefer? 💸 '}>
      <div className="flex flex-col  w-full items-center">
        <div className="mt-8 w-full flex flex-col gap-3">
          <Options
            selected={salesModel}
            values={options}
            onSelect={setSalesModel}
          />

          <Typography
            variant="body2"
            className="text-center mt-6 hidden md:block"
          >
            We want to customise your onboarding and tailor to your needs. You
            still be able to sell content in both Subscription and One Time
            Purchase model.
          </Typography>

          <PrimaryButton
            className={`w-full mt-5 z-10`}
            onClick={onRegister}
            autoFocus
            disabled={!complete}
          >
            {redirectState === 'redirecting' ? <Spinner /> : 'Continue'}
          </PrimaryButton>
        </div>
        <div className="absolute bottom-4">
          <Progress steps={3} stepsCompleted={3} />
        </div>
      </div>
    </AuthLayout>
  );
};
